<script lang="ts">
import { h } from 'vue'
import type { Delta, Op } from '~/domain/quill'

export default {
  name: 'DeltaRenderer',
  props: {
    delta: {
      type: Object,
    },
  },
  setup(props: { delta: Delta }) {
    const { isWebParts } = useWebParts()

    function inline(op: Op): VNode | string {
      let tag: string[] = []
      let cls: string[] = []
      let style: { [key: string]: string } = {}
      let link: string = ''
      if (op.attributes) {
        if (op.attributes['bold']) tag.push('strong')
        if (op.attributes['italic']) tag.push('em')
        if (op.attributes['underline']) tag.push('u')
        if (op.attributes['strike']) tag.push('s')
        if (op.attributes['code']) tag.push('code')
        if (op.attributes['link']) {
          link = op.attributes['link']
          tag.push('a')
        }
        if (op.attributes['script']) {
          if (op.attributes['script'] === 'sub') tag.push('sub')
          if (op.attributes['script'] === 'super') tag.push('sup')
        }
        if (op.attributes['background']) {
          style['background'] = op.attributes['background']
        }
        if (op.attributes['color']) {
          style['color'] = op.attributes['color']
        }
      }

      if (tag.length === 0) {
        if (cls.length === 0 && Object.keys(style).length === 0)
          return op.insert
        else return h('span', { class: cls, style: style }, op.insert)
      }
      let node: any = op.insert
      tag.forEach((t, ind) => {
        let props: any = {}
        if (ind == 0) {
          props.class = cls
          props.style = style
        }
        if (t === 'a' && link != null) {
          props.href = link
          if (isWebParts) {
            props.target = '_top'
          }
        }
        node = h(t, props, [node])
      })
      return node
    }

    function getListTag(lastListType: string) {
      switch (lastListType) {
        case 'bullet':
          return 'ul'
        case 'orderd':  // REVIEW orderedが正しいが、いったん保留
          return 'ol'
        case 'pre':
          return 'pre'
        default:
          return 'div'
      }
    }

    return () => {
      try {
        if (!props.delta || !props.delta.ops) return h('div')
        const doc: VNode[] = []
        let lineBuffer: Op[] = []
        let list = []
        let lastListType = ''
        props.delta.ops.forEach(op => {
          op.insert.split(/\n/).forEach((s, i) => {
            if (i > 0) {
              let tag = 'p'
              let cls = []
              let listType = ''
              if (op.attributes) {
                if (op.attributes['list']) {
                  tag = 'li'
                  listType = op.attributes['list']
                }
                if (op.attributes['code-block']) {
                  tag = 'code'
                  listType = 'pre'
                }
                if (op.attributes['blockquote']) {
                  tag = 'blockquote'
                }
                if (op.attributes['align']) {
                  if (op.attributes['align'] === 'right') {
                    cls.push('has-text-right')
                  } else if (op.attributes['align'] === 'center') {
                    cls.push('text-center')
                  } else if (op.attributes['align'] === 'justify') {
                    cls.push('has-text-justified')
                  }
                }
                if (op.attributes['indent']) {
                  cls.push('indent-' + op.attributes['indent'])
                }
              }
              let e = h(
                tag,
                { class: cls },
                lineBuffer.length > 0
                  ? lineBuffer.map(op => inline(op))
                  : [h('br')],
              )
              if (list.length > 0 && listType != lastListType) {
                doc.push(h(getListTag(lastListType), {}, list))
                list = []
              }
              if (listType) {
                list.push(e)
              } else {
                doc.push(e)
              }
              lastListType = listType
              lineBuffer = []
            }
            if (s.length > 0) {
              lineBuffer.push({ insert: s, attributes: op.attributes })
            }
          })
        })
        if (list.length > 0) doc.push(h(getListTag(lastListType), {}, list))

        return h('div', { class: ['delta-renderer', 'content'] }, doc)
      } catch (e) {
        console.error(e)
      }
    }
  },
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.delta-renderer {
  tab-size: 4;
  text-align: left;
  /* white-space: pre-wrap; */
  word-wrap: break-word;
  p,
  ol,
  ul,
  pre,
  blockquote,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0;
    padding: 0;
    counter-reset: list-1 list-2 list-3 list-4 list-5 list-6 list-7 list-8
      list-9;
  }
  ol,
  ul {
    padding-left: 1.5em;
  }
  ol > li,
  ul > li {
    list-style-type: none;
  }
  div > li {
    // 互換対応。CSSリセットで非表示になるので、ここで復元。
    list-style-type: inherit;
  }
  ul > li::before {
    content: '\2022';
  }
  ul[data-checked='true'],
  ul[data-checked='false'] {
    pointer-events: none;
  }
  ul[data-checked='true'] > li *,
  ul[data-checked='false'] > li * {
    pointer-events: all;
  }
  ul[data-checked='true'] > li::before,
  ul[data-checked='false'] > li::before {
    color: #777;
    cursor: pointer;
    pointer-events: all;
  }
  ul[data-checked='true'] > li::before {
    content: '\2611';
  }
  ul[data-checked='false'] > li::before {
    content: '\2610';
  }
  li::before {
    display: inline-block;
    white-space: nowrap;
    width: 1.2em;
  }
  ol li {
    counter-reset: list-1 list-2 list-3 list-4 list-5 list-6 list-7 list-8
      list-9;
    counter-increment: list-0;
  }
  ol li:before {
    content: counter(list-0, decimal) '. ';
  }
  ol li.indent-1 {
    counter-increment: list-1;
  }
  ol li.indent-1:before {
    content: counter(list-1, lower-alpha) '. ';
  }
  ol li.indent-1 {
    counter-reset: list-2 list-3 list-4 list-5 list-6 list-7 list-8 list-9;
  }
  ol li.indent-2 {
    counter-increment: list-2;
  }
  ol li.indent-2:before {
    content: counter(list-2, lower-roman) '. ';
  }
  ol li.indent-2 {
    counter-reset: list-3 list-4 list-5 list-6 list-7 list-8 list-9;
  }
  ol li.indent-3 {
    counter-increment: list-3;
  }
  ol li.indent-3:before {
    content: counter(list-3, decimal) '. ';
  }
  ol li.indent-3 {
    counter-reset: list-4 list-5 list-6 list-7 list-8 list-9;
  }
  ol li.indent-4 {
    counter-increment: list-4;
  }
  ol li.indent-4:before {
    content: counter(list-4, lower-alpha) '. ';
  }
  ol li.indent-4 {
    counter-reset: list-5 list-6 list-7 list-8 list-9;
  }
  ol li.indent-5 {
    counter-increment: list-5;
  }
  ol li.indent-5:before {
    content: counter(list-5, lower-roman) '. ';
  }
  ol li.indent-5 {
    counter-reset: list-6 list-7 list-8 list-9;
  }
  ol li.indent-6 {
    counter-increment: list-6;
  }
  ol li.indent-6:before {
    content: counter(list-6, decimal) '. ';
  }
  ol li.indent-6 {
    counter-reset: list-7 list-8 list-9;
  }
  ol li.indent-7 {
    counter-increment: list-7;
  }
  ol li.indent-7:before {
    content: counter(list-7, lower-alpha) '. ';
  }
  ol li.indent-7 {
    counter-reset: list-8 list-9;
  }
  ol li.indent-8 {
    counter-increment: list-8;
  }
  ol li.indent-8:before {
    content: counter(list-8, lower-roman) '. ';
  }
  ol li.indent-8 {
    counter-reset: list-9;
  }
  ol li.indent-9 {
    counter-increment: list-9;
  }
  ol li.indent-9:before {
    content: counter(list-9, decimal) '. ';
  }
  .indent-1:not(.direction-rtl) {
    padding-left: 3em;
  }
  li.indent-1:not(.direction-rtl) {
    padding-left: 4.5em;
  }
  .indent-1.direction-rtl.align-right {
    padding-right: 3em;
  }
  li.indent-1.direction-rtl.align-right {
    padding-right: 4.5em;
  }
  .indent-2:not(.direction-rtl) {
    padding-left: 6em;
  }
  li.indent-2:not(.direction-rtl) {
    padding-left: 7.5em;
  }
  .indent-2.direction-rtl.align-right {
    padding-right: 6em;
  }
  li.indent-2.direction-rtl.align-right {
    padding-right: 7.5em;
  }
  .indent-3:not(.direction-rtl) {
    padding-left: 9em;
  }
  li.indent-3:not(.direction-rtl) {
    padding-left: 10.5em;
  }
  .indent-3.direction-rtl.align-right {
    padding-right: 9em;
  }
  li.indent-3.direction-rtl.align-right {
    padding-right: 10.5em;
  }
  .indent-4:not(.direction-rtl) {
    padding-left: 12em;
  }
  li.indent-4:not(.direction-rtl) {
    padding-left: 13.5em;
  }
  .indent-4.direction-rtl.align-right {
    padding-right: 12em;
  }
  li.indent-4.direction-rtl.align-right {
    padding-right: 13.5em;
  }
  .indent-5:not(.direction-rtl) {
    padding-left: 15em;
  }
  li.indent-5:not(.direction-rtl) {
    padding-left: 16.5em;
  }
  .indent-5.direction-rtl.align-right {
    padding-right: 15em;
  }
  li.indent-5.direction-rtl.align-right {
    padding-right: 16.5em;
  }
  .indent-6:not(.direction-rtl) {
    padding-left: 18em;
  }
  li.indent-6:not(.direction-rtl) {
    padding-left: 19.5em;
  }
  .indent-6.direction-rtl.align-right {
    padding-right: 18em;
  }
  li.indent-6.direction-rtl.align-right {
    padding-right: 19.5em;
  }
  .indent-7:not(.direction-rtl) {
    padding-left: 21em;
  }
  li.indent-7:not(.direction-rtl) {
    padding-left: 22.5em;
  }
  .indent-7.direction-rtl.align-right {
    padding-right: 21em;
  }
  li.indent-7.direction-rtl.align-right {
    padding-right: 22.5em;
  }
  .indent-8:not(.direction-rtl) {
    padding-left: 24em;
  }
  li.indent-8:not(.direction-rtl) {
    padding-left: 25.5em;
  }
  .indent-8.direction-rtl.align-right {
    padding-right: 24em;
  }
  li.indent-8.direction-rtl.align-right {
    padding-right: 25.5em;
  }
  .indent-9:not(.direction-rtl) {
    padding-left: 27em;
  }
  li.indent-9:not(.direction-rtl) {
    padding-left: 28.5em;
  }
  .indent-9.direction-rtl.align-right {
    padding-right: 27em;
  }
  li.indent-9.direction-rtl.align-right {
    padding-right: 28.5em;
  }
  em {
    font-family:
      Times New Roman,
      'ＭＳ Ｐゴシック';
  }
  .blank::before {
    color: rgba(0, 0, 0, 0.6);
    content: attr(data-placeholder);
    font-style: italic;
    left: 15px;
    pointer-events: none;
    position: absolute;
    right: 15px;
  }

  blockquote {
    padding: calc(0.25rem * 2);
    @include is-tablet {
      padding: calc(0.25rem * 4);
    }
  }

  code {
    background-color: #23241f;
    display: block;
    color: #f8f8f2;
    overflow-x: scroll;
    margin-bottom: 5px;
    margin-top: 5px;
    padding: 5px 10px;
  }
}
</style>
